import { useRef, useEffect, useState } from "react"
import { Label } from "@/components/ui/label"
import { Check, X, Pencil } from "lucide-react"
import { cn } from "@/lib/utils"
import { useConfirmableEmail } from "@/hooks/use-confirmable-email"
import { useTranslation } from "@/hooks/use-translation"
import { ChangedPill } from "./display-helpers"

export interface ConfirmableEmailFieldProps {
  value: string
  onConfirm: (email: string) => void
  error?: string
  required?: boolean
  atsHint?: React.ReactNode
  changedPill?: boolean
  /** Notifies the parent whenever the field enters or leaves the editing-with-pending-change state. The parent can use this to gate form submission so users can't proceed with un-confirmed email edits sitting in the input. */
  onEditingChange?: (isEditing: boolean) => void
  /** Synchronous pencil-click predicate. Return false to BLOCK entering edit mode (the parent
   * usually renders an inline confirmation when this happens, then triggers editing later via
   * `forceStartEditingTrigger`). When omitted, the pencil starts editing immediately. */
  onAttemptStartEdit?: () => boolean
  /** When this prop's identity changes, the field forcibly enters edit mode. Pairs with
   * `onAttemptStartEdit` for the deferred-confirmation flow: parent gates the pencil click,
   * shows a confirmation, then increments this prop on Yes to actually start editing. */
  forceStartEditingTrigger?: unknown
}

export function ConfirmableEmailField({
  value,
  onConfirm,
  error,
  required,
  atsHint,
  changedPill,
  onEditingChange,
  onAttemptStartEdit,
  forceStartEditingTrigger,
}: ConfirmableEmailFieldProps) {
  const { t } = useTranslation()
  const {
    isEditing,
    draftValue,
    draftIsValid,
    canCancel,
    displayError,
    startEditing,
    confirm,
    cancel,
    changeDraft,
  } = useConfirmableEmail({ value, onConfirm, error })

  const inputRef = useRef<HTMLInputElement>(null)
  const [hasUserEdited, setHasUserEdited] = useState(false)

  const wrappedStartEditing = () => {
    // Parent gate: when onAttemptStartEdit is provided and returns false, the pencil click is
    // intercepted (the parent surfaces an inline confirmation and triggers editing later via
    // forceStartEditingTrigger if the user accepts). Without the gate the pencil starts edit
    // immediately, which is the previous behavior.
    if (onAttemptStartEdit && !onAttemptStartEdit()) return
    setHasUserEdited(true)
    startEditing()
  }

  useEffect(() => {
    if (isEditing && hasUserEdited) {
      inputRef.current?.focus()
    }
  }, [isEditing, hasUserEdited])

  // Forced-editing entry point used by the parent's deferred-confirmation flow. We track the
  // initial trigger value so the first render doesn't auto-start editing — only subsequent
  // identity changes do. Pairs with onAttemptStartEdit: parent intercepts the pencil click,
  // shows a confirmation, then increments this prop on accept to actually enter edit mode.
  const initialForceTriggerRef = useRef(forceStartEditingTrigger)
  useEffect(() => {
    if (forceStartEditingTrigger === initialForceTriggerRef.current) return
    initialForceTriggerRef.current = forceStartEditingTrigger
    setHasUserEdited(true)
    startEditing()
  }, [forceStartEditingTrigger, startEditing])

  // Lift the editing state out so callers can block form submission while the user
  // has un-confirmed email edits open.
  useEffect(() => {
    onEditingChange?.(isEditing)
  }, [isEditing, onEditingChange])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      confirm()
    } else if (e.key === "Escape" && canCancel) {
      e.preventDefault()
      cancel()
    }
  }

  if (isEditing) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-sm font-medium text-gray-700">
            {t("labelEmailAddress")}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          {atsHint}
          {changedPill && <ChangedPill />}
        </div>
        <div className={cn(
          "flex items-center rounded-md border bg-background ring-offset-background",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          displayError
            ? "border-red-300 focus-within:ring-red-400"
            : "border-input",
        )}>
          <input
            ref={inputRef}
            type="email"
            value={draftValue}
            onChange={(e) => changeDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("placeholderEmail")}
            className="flex-1 h-9 px-3 text-sm bg-transparent outline-none border-0 placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={confirm}
            disabled={!draftIsValid}
            className={cn(
              "h-7 w-7 my-1 flex items-center justify-center rounded transition-colors shrink-0",
              draftIsValid
                ? "text-green-600 hover:bg-gray-100"
                : "text-gray-300 cursor-not-allowed",
            )}
            aria-label={t("confirmEmail")}
          >
            <Check size={16} />
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={!canCancel}
            className={cn(
              "h-7 w-7 my-1 mr-1 flex items-center justify-center rounded transition-colors shrink-0",
              canCancel
                ? "text-red-500 hover:bg-gray-100"
                : "text-gray-300 cursor-not-allowed",
            )}
            aria-label={t("cancelEmailEdit")}
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-red-500 min-h-4">{displayError}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-sm font-medium text-gray-700">
          {t("labelEmailAddress")}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {atsHint}
        {changedPill && <ChangedPill />}
      </div>
      <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-200 bg-gray-50">
        <span className="text-sm text-gray-700 flex-1 truncate">{value}</span>
        <button
          type="button"
          onClick={wrappedStartEditing}
          className="p-1 rounded transition-colors text-gray-400 hover:text-gray-600"
          aria-label={t("editEmail")}
        >
          <Pencil size={14} />
        </button>
      </div>
      <p className="text-xs text-red-500 min-h-4">{error}</p>
    </div>
  )
}
